import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus, UserCog, UserCheck, GraduationCap, Sparkles, TrendingUp,
  Phone, Mail, ShieldCheck, ShieldAlert, Star, Hourglass, Download,
} from "lucide-react";
import { api } from "../lib/api";
import { Modal } from "../components/Modal";
import { useAuth } from "../lib/auth";
import { toCsv, downloadCsv, dateStamp } from "../lib/csv";
import { LEADER_KINDS, leaderKindLabel } from "../lib/constants";

interface Leader {
  id: string; name: string; email: string | null; phone: string | null;
  status: "pending" | "in_training" | "active" | "potential";
  approved_at: number | null;
  training_confirmed_at: number | null;
  is_cdm: number;
  kind: "disciple_maker" | "cdm" | "catalyst" | "campaign_manager";
  groups_leading: number; total_people: number;
  completed_required: number; total_required: number;
}

export default function Leaders() {
  const { user } = useAuth();
  const [leaders, setLeaders] = useState<Leader[]>([]);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Leader | null>(null);

  const canApprove = user?.role === "owner" || user?.role === "admin";

  const load = async () => {
    const r = await api.get<{ leaders: Leader[] }>("/leaders");
    setLeaders(r.leaders);
  };
  useEffect(() => { load(); }, []);

  const totalPeople = leaders.reduce((s, l) => s + (l.total_people ?? 0), 0);
  const pending = leaders.filter((l) => !l.approved_at && l.status !== "potential");
  const inTraining = leaders.filter((l) => l.approved_at && !l.training_confirmed_at);
  const active = leaders.filter((l) => l.approved_at && l.training_confirmed_at);
  const potential = leaders.filter((l) => l.status === "potential");
  const cdms = leaders.filter((l) => l.is_cdm);
  const peoplePerLeader = active.length ? +(totalPeople / active.length).toFixed(1) : 0;

  const approve = async (id: string) => {
    await api.post(`/leaders/${id}/approve`);
    load();
  };
  const revoke = async (id: string) => {
    if (!confirm("Revoke approval? They'll go back to pending.")) return;
    await api.post(`/leaders/${id}/revoke`);
    load();
  };
  const unconfirm = async (id: string) => {
    if (!confirm("Mark training as not yet reviewed? Leader returns to In Training.")) return;
    await api.post(`/leaders/${id}/unconfirm-training`);
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Leader Register</h1>
          <p className="text-ink-500 text-sm">Add leaders, approve them, track training, and activate</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="btn-secondary"
            disabled={leaders.length === 0}
            onClick={() => {
              const statusLabel = (l: Leader) =>
                l.training_confirmed_at ? "Active" :
                l.approved_at ? "In Training" :
                l.status === "potential" ? "Potential" : "Pending";
              const csv = toCsv(
                leaders.map((l) => ({
                  name: l.name, email: l.email, phone: l.phone,
                  status: statusLabel(l),
                  role: leaderKindLabel(l.kind ?? (l.is_cdm ? "cdm" : "disciple_maker")),
                  groups_leading: l.groups_leading,
                  total_people: l.total_people,
                  training: `${l.completed_required ?? 0}/${l.total_required ?? 0}`,
                })),
                [
                  { key: "name",           header: "Name" },
                  { key: "phone",          header: "Phone" },
                  { key: "email",          header: "Email" },
                  { key: "status",         header: "Status" },
                  { key: "role",           header: "Role" },
                  { key: "groups_leading", header: "Groups Leading" },
                  { key: "total_people",   header: "Total People" },
                  { key: "training",       header: "Required Training" },
                ],
              );
              downloadCsv(`leaders-${dateStamp()}.csv`, csv);
            }}
            title="Download leaders as CSV"
          >
            <Download className="h-4 w-4" /> <span className="hidden sm:inline">Export CSV</span>
          </button>
          <button className="btn-primary" onClick={() => setAdding(true)}><Plus className="h-4 w-4" /> Add Leader</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Mini label="Total" value={leaders.length} icon={<UserCog className="h-5 w-5" />} />
        <Mini label="Pending" value={pending.length} accent="text-amber-700" icon={<Hourglass className="h-5 w-5" />} />
        <Mini label="In Training" value={inTraining.length} accent="text-blue-700" icon={<GraduationCap className="h-5 w-5" />} />
        <Mini label="Active" value={active.length} accent="text-green-700" icon={<UserCheck className="h-5 w-5" />} />
        <Mini label="Campaign DMs" value={cdms.length} accent="text-brand" icon={<Star className="h-5 w-5" />} />
        <Mini label="People / Leader" value={peoplePerLeader} accent="text-ink-900" icon={<TrendingUp className="h-5 w-5" />} />
      </div>

      {pending.length > 0 && (
        <Section title={`Pending Approval (${pending.length})`} dot="bg-amber-500" intro={canApprove ? "Review and approve to begin training." : "Waiting on an admin to approve."}>
          <Cards items={pending} onClick={setEditing} canApprove={canApprove} onApprove={approve} onRevoke={revoke} onUnconfirm={unconfirm} />
        </Section>
      )}
      <Section title={`Leaders in Training (${inTraining.length})`} dot="bg-blue-500" intro="Approved — completing required training modules.">
        <Cards items={inTraining} onClick={setEditing} canApprove={canApprove} onApprove={approve} onRevoke={revoke} onUnconfirm={unconfirm} />
      </Section>
      <Section title={`Active Leaders (${active.length})`} dot="bg-green-500">
        <Cards items={active} onClick={setEditing} canApprove={canApprove} onApprove={approve} onRevoke={revoke} onUnconfirm={unconfirm} />
      </Section>
      <Section title={`Potential Leaders (${potential.length})`} dot="bg-purple-500" intro="Identified but not yet put forward for approval.">
        <Cards items={potential} onClick={setEditing} canApprove={canApprove} onApprove={approve} onRevoke={revoke} onUnconfirm={unconfirm} />
      </Section>

      <Modal open={adding} onClose={() => setAdding(false)} title="Add Leader">
        <LeaderForm onSaved={() => { setAdding(false); load(); }} onCancel={() => setAdding(false)} />
      </Modal>
      <Modal open={!!editing} onClose={() => setEditing(null)} title="Edit Leader">
        {editing && (
          <LeaderForm initial={editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)}
            onDelete={async () => {
              if (!confirm("Delete this leader?")) return;
              await api.del(`/leaders/${editing.id}`);
              setEditing(null); load();
            }} />
        )}
      </Modal>
    </div>
  );
}

function Section({ title, dot, intro, children }: { title: string; dot: string; intro?: string; children: any }) {
  return (
    <section>
      <h2 className="font-medium text-ink-700 flex items-center gap-2 mb-1">
        <span className={`h-2 w-2 rounded-full ${dot}`} />{title}
      </h2>
      {intro && <p className="text-xs text-ink-500 mb-3">{intro}</p>}
      {children}
    </section>
  );
}

interface CardsProps {
  items: Leader[];
  onClick: (l: Leader) => void;
  canApprove: boolean;
  onApprove: (id: string) => void;
  onRevoke: (id: string) => void;
  onUnconfirm: (id: string) => void;
}

function Cards({ items, onClick, canApprove, onApprove, onRevoke, onUnconfirm }: CardsProps) {
  if (items.length === 0) return <div className="text-sm text-ink-400">None.</div>;
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((l) => {
        const pct = l.total_required ? Math.round((l.completed_required / l.total_required) * 100) : 0;
        const stateLabel = !l.approved_at && l.status !== "potential" ? "Pending Approval"
          : l.status === "potential" ? "Potential"
          : l.training_confirmed_at ? "Active"
          : "In Training";
        const stateClass =
          stateLabel === "Active" ? "bg-green-100 text-green-800"
          : stateLabel === "In Training" ? "bg-blue-100 text-blue-800"
          : stateLabel === "Pending Approval" ? "bg-amber-100 text-amber-800"
          : "bg-purple-100 text-purple-800";
        return (
          <div key={l.id} className="card p-5 hover:shadow-md transition">
            <div className="flex items-center justify-between gap-2">
              <button onClick={() => onClick(l)} className="font-semibold text-left hover:text-brand truncate">{l.name}</button>
              <div className="flex items-center gap-1 shrink-0">
                <KindChip kind={l.kind ?? (l.is_cdm === 1 ? "cdm" : "disciple_maker")} />
                <span className={`badge ${stateClass}`}>{stateLabel}</span>
              </div>
            </div>
            <div className="mt-2 text-sm text-ink-700 space-y-1">
              {l.phone && <div className="flex items-center gap-1.5"><Phone className="h-3 w-3 text-ink-400" />{l.phone}</div>}
              {l.email && <div className="flex items-center gap-1.5 text-xs text-ink-500"><Mail className="h-3 w-3" />{l.email}</div>}
            </div>
            <div className="mt-3 flex justify-between text-sm">
              <div><div className="text-ink-500 text-xs">Groups Leading</div><div className="font-medium">{l.groups_leading}</div></div>
              <div><div className="text-ink-500 text-xs">Total People</div><div className="font-medium">{l.total_people}</div></div>
            </div>
            <div className="mt-3">
              <div className="flex justify-between text-xs text-ink-500">
                <span>Required Training</span>
                <span>{l.completed_required}/{l.total_required} · {pct}%</span>
              </div>
              <div className="h-2 bg-ink-100 rounded-full overflow-hidden mt-1">
                <div className="h-full bg-brand transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>
            {canApprove && (
              <div className="mt-4 pt-3 border-t border-ink-100 flex flex-wrap gap-2">
                {!l.approved_at && (
                  <button onClick={() => onApprove(l.id)} className="btn-primary text-xs px-3 py-1.5">
                    <ShieldCheck className="h-3.5 w-3.5" /> Approve
                  </button>
                )}
                {l.approved_at && !l.training_confirmed_at && (
                  <Link to="/training" className="btn-secondary text-xs px-3 py-1.5">
                    <GraduationCap className="h-3.5 w-3.5" /> Open Training
                  </Link>
                )}
                {l.training_confirmed_at && (
                  <button onClick={() => onUnconfirm(l.id)} className="btn-ghost text-xs px-3 py-1.5 text-amber-700">
                    Unconfirm training
                  </button>
                )}
                {l.approved_at && (
                  <button onClick={() => onRevoke(l.id)} className="btn-ghost text-xs px-3 py-1.5 text-brand">
                    <ShieldAlert className="h-3.5 w-3.5" /> Revoke
                  </button>
                )}
                <Link to="/training" className="btn-ghost text-xs px-3 py-1.5 ml-auto">View training →</Link>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function KindChip({ kind }: { kind: string }) {
  const k = LEADER_KINDS.find((x) => x.id === kind) ?? LEADER_KINDS[0];
  // Compact label for the badge — full name takes too much room next to status
  const compact: Record<string, string> = {
    disciple_maker: "DM",
    cdm: "CDM",
    catalyst: "Catalyst",
    campaign_manager: "Campaign Mgr",
  };
  return (
    <span className={`badge ${k.chip}`} title={k.label}>
      <span className={`h-1.5 w-1.5 rounded-full mr-1 ${k.dot}`} />
      {compact[k.id] ?? k.label}
    </span>
  );
}

function Mini({ label, value, accent = "text-ink-900", icon }: { label: string; value: number | string; accent?: string; icon?: React.ReactNode }) {
  return (
    <div className="card p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className={`text-2xl font-semibold ${accent}`}>{value}</div>
          <div className="text-xs text-ink-500 mt-1">{label}</div>
        </div>
        {icon && <div className="text-ink-300">{icon}</div>}
      </div>
    </div>
  );
}

function LeaderForm({
  initial, onSaved, onCancel, onDelete,
}: {
  initial?: Partial<Leader>; onSaved: () => void; onCancel: () => void; onDelete?: () => void;
}) {
  const [form, setForm] = useState({
    name: initial?.name ?? "",
    email: initial?.email ?? "",
    phone: initial?.phone ?? "",
    status: (initial?.status as string) ?? "pending",
    kind: (initial?.kind as string) ?? "disciple_maker",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const payload = { ...form };
      if (initial && (initial as Leader).id) await api.patch(`/leaders/${(initial as Leader).id}`, payload);
      else await api.post("/leaders", payload);
      onSaved();
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="space-y-3">
      <div>
        <label className="label">Name</label>
        <input className="input" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Phone</label>
          <input className="input" value={form.phone ?? ""} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
        </div>
        <div>
          <label className="label">Email</label>
          <input className="input" type="email" value={form.email ?? ""} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
        </div>
      </div>
      <div>
        <label className="label">Pipeline status</label>
        <select className="input" value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as any }))}>
          <option value="potential">Potential — identified but not yet put forward</option>
          <option value="pending">Pending — awaiting approval</option>
        </select>
        <p className="text-xs text-ink-500 mt-1">Status moves to In Training and Active automatically as the leader is approved and completes training.</p>
      </div>
      <div className="mt-2">
        <label className="label">Leader role</label>
        <div className="grid sm:grid-cols-2 gap-2">
          {LEADER_KINDS.map((k) => {
            const selected = form.kind === k.id;
            return (
              <label key={k.id} className={`cursor-pointer rounded-md ring-1 px-3 py-2.5 text-sm flex items-start gap-2 transition ${
                selected ? "ring-brand bg-brand-50/50" : "ring-ink-200 hover:bg-ink-50"
              }`}>
                <input
                  type="radio"
                  className="mt-1"
                  checked={selected}
                  onChange={() => setForm((f) => ({ ...f, kind: k.id }))}
                />
                <span>
                  <span className="flex items-center gap-1.5 font-medium">
                    <span className={`h-2 w-2 rounded-full ${k.dot}`} />
                    {k.label}
                  </span>
                  <span className="block text-xs text-ink-500 mt-0.5">{k.blurb}</span>
                </span>
              </label>
            );
          })}
        </div>
        <p className="text-xs text-ink-500 mt-2">
          Only <strong>Campaign Disciple Makers</strong> are eligible to receive campaign-generated leads in the Leads inbox.
        </p>
      </div>
      {err && <div className="text-sm text-brand">{err}</div>}
      <div className="flex justify-between pt-2">
        <div>{onDelete && <button type="button" onClick={onDelete} className="text-brand text-sm">Delete</button>}</div>
        <div className="flex gap-2">
          <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="btn-primary" disabled={busy}>{busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </form>
  );
}
